/** App-styled confirm / prompt dialogs (replaces window.confirm / window.prompt). */

const OVERLAY_ID = "sfe-confirm-dialog-overlay";
/** Same marker as custom panels so host DOM cleanup does not strip the dialog. */
const CUSTOM_PANEL_MARKER_ATTR = "data-sfe-custom-panel";

export type ConfirmDialogOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Styles the confirm action as destructive (delete / reset). */
  danger?: boolean;
};

export type ChoiceDialogOption = {
  id: string;
  label: string;
  hint?: string;
};

export type ChoiceDialogOptions = {
  title?: string;
  message?: string;
  options: ChoiceDialogOption[];
  cancelLabel?: string;
};

export type PromptDialogOptions = {
  title?: string;
  message?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  placeholder?: string;
  maxLength?: number;
};

export type TextAreaDialogOptions = {
  title?: string;
  message?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  /** When false (default), empty submit is rejected. When true, empty string is allowed. */
  allowEmpty?: boolean;
  fieldLabel?: string;
};

export type ContentDialogOptions = {
  title?: string;
  message?: string;
  /** Trusted HTML body (caller must escape user/API content). */
  bodyHtml: string;
  closeLabel?: string;
  /** Extra HTML before the close button in the footer. */
  footerHtml?: string;
  /** Handle per-row actions in bodyHtml (`data-sfe-advice-action`, `data-rec-id`, `data-agent-id`). Return true to close. */
  onBodyAction?: (
    action: string,
    recId: string,
    agentId: string,
    button: HTMLElement,
  ) => void | boolean | Promise<void | boolean>;
  /** Handle footer actions (`data-sfe-advice-action` on footer buttons). Return true to close. */
  onFooterAction?: (action: string, button: HTMLElement) => void | boolean | Promise<void | boolean>;
  /** Extra class on the modal shell (e.g. for wider layouts). */
  modalClass?: string;
};

export type InstructionsTemplateOption = {
  id: string;
  name: string;
  /** Short slot prompt (every advise). */
  body: string;
  /** Standing bootstrap prompt (Agent.create only). */
  bootstrapBody: string;
  builtIn?: boolean;
  updatedAt?: string;
};

export type InstructionsDialogResult = {
  instructions: string;
  promptTemplateId: string;
  /** Working set after in-modal create/rename/edit/delete. */
  templates: InstructionsTemplateOption[];
  /** Ids removed in the modal (existing server ids only). */
  deletedIds: string[];
  /** Built-in template was reset to seed body. */
  resetBuiltin: boolean;
};

export type InstructionsDialogOptions = {
  title?: string;
  message?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  fieldLabel?: string;
  /** Available cloud prompt templates (user-level). */
  templates: InstructionsTemplateOption[];
  /** Currently selected template id for this agent. */
  selectedTemplateId: string;
  /** Seed slot body for Reset on the built-in Wheel template. */
  seedBody?: string;
  /** Seed bootstrap body for Reset on the built-in Wheel template. */
  seedBootstrapBody?: string;
  /** Placeholder legend shown under the template editor. */
  placeholderLegend?: string;
  templateLabel?: string;
  templateHint?: string;
  bootstrapLabel?: string;
  bootstrapHint?: string;
  slotLabel?: string;
  slotHint?: string;
  instructionsHint?: string;
};

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function removeOverlay(): void {
  document.getElementById(OVERLAY_ID)?.remove();
}

function mountOverlay(innerHtml: string): HTMLElement {
  removeOverlay();
  const overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.className = "sfe-modal-overlay sfe-modal-open sfe-confirm-overlay";
  overlay.setAttribute(CUSTOM_PANEL_MARKER_ATTR, "true");
  overlay.setAttribute("data-sfe-injected", "true");
  overlay.innerHTML = innerHtml;
  document.body.appendChild(overlay);
  return overlay;
}

/**
 * Show a confirm dialog. Resolves true if confirmed, false if cancelled.
 */
export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  const title = options.title?.trim() || "Confirm";
  const confirmLabel = options.confirmLabel?.trim() || "Confirm";
  const cancelLabel = options.cancelLabel?.trim() || "Cancel";
  const confirmClass = options.danger
    ? "sfe-btn sfe-btn-danger"
    : "sfe-btn sfe-btn-primary";

  return new Promise((resolve) => {
    const overlay = mountOverlay(`
      <div class="sfe-modal sfe-confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="sfe-confirm-title" aria-describedby="sfe-confirm-message">
        <div class="sfe-modal-header">
          <h3 id="sfe-confirm-title">${esc(title)}</h3>
          <button type="button" class="sfe-modal-close" data-sfe-confirm="cancel" aria-label="Close">×</button>
        </div>
        <p class="sfe-confirm-message" id="sfe-confirm-message">${esc(options.message)}</p>
        <div class="sfe-modal-actions">
          <button type="button" class="sfe-btn" data-sfe-confirm="cancel">${esc(cancelLabel)}</button>
          <button type="button" class="${confirmClass}" data-sfe-confirm="ok" autofocus>${esc(confirmLabel)}</button>
        </div>
      </div>
    `);

    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey, true);
      removeOverlay();
      resolve(value);
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        finish(false);
      } else if (event.key === "Enter") {
        const t = event.target;
        if (t instanceof HTMLButtonElement && t.dataset.sfeConfirm === "cancel") return;
        event.preventDefault();
        finish(true);
      }
    };

    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        finish(false);
        return;
      }
      const action = target.closest<HTMLElement>("[data-sfe-confirm]")?.dataset.sfeConfirm;
      if (action === "ok") finish(true);
      if (action === "cancel") finish(false);
    });

    document.addEventListener("keydown", onKey, true);
    overlay.querySelector<HTMLButtonElement>('[data-sfe-confirm="ok"]')?.focus();
  });
}

/**
 * Pick one option from a list. Resolves the option id, or null on cancel.
 */
export function choiceDialog(options: ChoiceDialogOptions): Promise<string | null> {
  const title = options.title?.trim() || "Choose";
  const cancelLabel = options.cancelLabel?.trim() || "Cancel";
  const items = (options.options || []).filter((o) => o.id && o.label);
  const list = items
    .map(
      (o, i) => `
        <button type="button" class="sfe-btn sfe-choice-option" data-sfe-choice="${esc(o.id)}"${i === 0 ? " autofocus" : ""}>
          <span class="sfe-choice-option-label">${esc(o.label)}</span>
          ${o.hint ? `<span class="muted small sfe-choice-option-hint">${esc(o.hint)}</span>` : ""}
        </button>`,
    )
    .join("");

  return new Promise((resolve) => {
    const overlay = mountOverlay(`
      <div class="sfe-modal sfe-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-choice-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-choice-title">${esc(title)}</h3>
          <button type="button" class="sfe-modal-close" data-sfe-confirm="cancel" aria-label="Close">×</button>
        </div>
        ${
          options.message
            ? `<p class="sfe-confirm-message" id="sfe-choice-message">${esc(options.message)}</p>`
            : ""
        }
        <div class="sfe-choice-list" role="list">${list || `<p class="muted small">No options.</p>`}</div>
        <div class="sfe-modal-actions">
          <button type="button" class="sfe-btn" data-sfe-confirm="cancel">${esc(cancelLabel)}</button>
        </div>
      </div>
    `);

    let settled = false;
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey, true);
      removeOverlay();
      resolve(value);
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        finish(null);
      }
    };

    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        finish(null);
        return;
      }
      if (target.closest<HTMLElement>("[data-sfe-confirm='cancel']")) {
        finish(null);
        return;
      }
      const choice = target.closest<HTMLElement>("[data-sfe-choice]")?.dataset.sfeChoice;
      if (choice) finish(choice);
    });

    document.addEventListener("keydown", onKey, true);
    overlay.querySelector<HTMLButtonElement>("[data-sfe-choice]")?.focus();
  });
}

/**
 * Show a prompt dialog. Resolves trimmed string on confirm, null on cancel.
 */
export function promptDialog(options: PromptDialogOptions): Promise<string | null> {
  const title = options.title?.trim() || "Enter a value";
  const confirmLabel = options.confirmLabel?.trim() || "OK";
  const cancelLabel = options.cancelLabel?.trim() || "Cancel";
  const maxLength = options.maxLength && options.maxLength > 0 ? options.maxLength : 80;
  const defaultValue = options.defaultValue ?? "";

  return new Promise((resolve) => {
    const overlay = mountOverlay(`
      <div class="sfe-modal sfe-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-prompt-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-prompt-title">${esc(title)}</h3>
          <button type="button" class="sfe-modal-close" data-sfe-confirm="cancel" aria-label="Close">×</button>
        </div>
        ${
          options.message
            ? `<p class="sfe-confirm-message" id="sfe-prompt-message">${esc(options.message)}</p>`
            : ""
        }
        <label class="sfe-modal-field">
          <span class="sfe-confirm-field-label">Name</span>
          <input
            type="text"
            class="sfe-api-key-input sfe-confirm-input"
            data-sfe-prompt-input
            maxlength="${maxLength}"
            value="${esc(defaultValue)}"
            placeholder="${esc(options.placeholder || "")}"
            autocomplete="off"
          />
        </label>
        <div class="sfe-modal-actions">
          <button type="button" class="sfe-btn" data-sfe-confirm="cancel">${esc(cancelLabel)}</button>
          <button type="button" class="sfe-btn sfe-btn-primary" data-sfe-confirm="ok">${esc(confirmLabel)}</button>
        </div>
      </div>
    `);

    const input = overlay.querySelector<HTMLInputElement>("[data-sfe-prompt-input]");
    let settled = false;
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey, true);
      removeOverlay();
      resolve(value);
    };

    const submit = () => {
      const raw = (input?.value ?? "").trim().replace(/\s+/g, " ");
      if (!raw) {
        input?.focus();
        input?.select();
        return;
      }
      finish(raw.slice(0, maxLength));
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        finish(null);
      } else if (event.key === "Enter") {
        event.preventDefault();
        submit();
      }
    };

    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        finish(null);
        return;
      }
      const action = target.closest<HTMLElement>("[data-sfe-confirm]")?.dataset.sfeConfirm;
      if (action === "ok") submit();
      if (action === "cancel") finish(null);
    });

    document.addEventListener("keydown", onKey, true);
    requestAnimationFrame(() => {
      input?.focus();
      input?.select();
    });
  });
}

/**
 * Multi-line text dialog. Resolves string on confirm (may be empty if allowEmpty),
 * null on cancel. Enter inserts a newline; Ctrl/Cmd+Enter confirms.
 */
export function textAreaDialog(options: TextAreaDialogOptions): Promise<string | null> {
  const title = options.title?.trim() || "Edit text";
  const confirmLabel = options.confirmLabel?.trim() || "Save";
  const cancelLabel = options.cancelLabel?.trim() || "Cancel";
  const maxLength =
    options.maxLength && options.maxLength > 0 ? options.maxLength : 8000;
  const rows = options.rows && options.rows > 0 ? options.rows : 12;
  const allowEmpty = options.allowEmpty === true;
  const fieldLabel = options.fieldLabel?.trim() || "Text";
  const defaultValue = options.defaultValue ?? "";

  return new Promise((resolve) => {
    const overlay = mountOverlay(`
      <div class="sfe-modal sfe-confirm-modal sfe-textarea-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-textarea-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-textarea-title">${esc(title)}</h3>
          <button type="button" class="sfe-modal-close" data-sfe-confirm="cancel" aria-label="Close">×</button>
        </div>
        ${
          options.message
            ? `<p class="sfe-confirm-message" id="sfe-textarea-message">${esc(options.message)}</p>`
            : ""
        }
        <label class="sfe-modal-field sfe-textarea-field">
          <span class="sfe-confirm-field-label">${esc(fieldLabel)}</span>
          <textarea
            class="sfe-api-key-input sfe-confirm-input sfe-confirm-textarea"
            data-sfe-textarea-input
            maxlength="${maxLength}"
            rows="${rows}"
            placeholder="${esc(options.placeholder || "")}"
            spellcheck="true"
          >${esc(defaultValue)}</textarea>
        </label>
        <p class="muted small sfe-textarea-hint">Ctrl/⌘+Enter to save · Esc to cancel</p>
        <div class="sfe-modal-actions">
          <button type="button" class="sfe-btn" data-sfe-confirm="cancel">${esc(cancelLabel)}</button>
          <button type="button" class="sfe-btn sfe-btn-primary" data-sfe-confirm="ok">${esc(confirmLabel)}</button>
        </div>
      </div>
    `);

    const input = overlay.querySelector<HTMLTextAreaElement>("[data-sfe-textarea-input]");
    let settled = false;
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey, true);
      removeOverlay();
      resolve(value);
    };

    const submit = () => {
      const raw = (input?.value ?? "")
        .replace(/\r\n/g, "\n")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
      if (!raw && !allowEmpty) {
        input?.focus();
        return;
      }
      finish(raw.slice(0, maxLength));
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        finish(null);
      } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        submit();
      }
    };

    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        finish(null);
        return;
      }
      const action = target.closest<HTMLElement>("[data-sfe-confirm]")?.dataset.sfeConfirm;
      if (action === "ok") submit();
      if (action === "cancel") finish(null);
    });

    document.addEventListener("keydown", onKey, true);
    requestAnimationFrame(() => {
      input?.focus();
      const len = input?.value.length ?? 0;
      input?.setSelectionRange(len, len);
    });
  });
}

/**
 * Instructions editor + editable cloud prompt templates (bootstrap + slot + additional).
 * Resolves { instructions, promptTemplateId, templates } on save, null on cancel.
 */
export function instructionsDialog(
  options: InstructionsDialogOptions,
): Promise<InstructionsDialogResult | null> {
  const title = options.title?.trim() || "Instructions";
  const confirmLabel = options.confirmLabel?.trim() || "Save";
  const cancelLabel = options.cancelLabel?.trim() || "Cancel";
  const maxLength =
    options.maxLength && options.maxLength > 0 ? options.maxLength : 8000;
  const rows = options.rows && options.rows > 0 ? options.rows : 8;
  const fieldLabel = options.fieldLabel?.trim() || "Additional instructions";
  const templateLabel = options.templateLabel?.trim() || "Current template";
  const templateHint =
    options.templateHint?.trim() ||
    "Bootstrap is sent once when a Cursor agent is created; the slot template is sent every advise with live book data.";
  const bootstrapLabel = options.bootstrapLabel?.trim() || "Bootstrap prompt";
  const bootstrapHint =
    options.bootstrapHint?.trim() ||
    "Standing role, strategy rules, and JSON schema. Sent once on Agent.create (skipped on resume). Prefer strategy placeholders — avoid live book tokens.";
  const slotLabel = options.slotLabel?.trim() || "Slot template";
  const slotHint =
    options.slotHint?.trim() ||
    "Short timeslot pack: cash, paper positions/owedShares, SF+ candidates, want, memory, prior <FEEDBACK>, settings. Book is private paper ledger only.";
  const instructionsHint =
    options.instructionsHint?.trim() ||
    "Injected into <USER_INSTRUCTIONS> for this agent only.";
  const legend = options.placeholderLegend?.trim() || "";
  const defaultValue = options.defaultValue ?? "";

  /** Working copy of templates (edits stay local until Save). */
  let templates: InstructionsTemplateOption[] = (options.templates || []).map((t) => ({
    ...t,
    bootstrapBody: t.bootstrapBody || options.seedBootstrapBody || "",
    body: t.body || options.seedBody || "",
  }));
  if (!templates.length) {
    templates = [
      {
        id: options.selectedTemplateId || "wheel-default",
        name: "Wheel (default)",
        bootstrapBody: options.seedBootstrapBody || "",
        body: options.seedBody || "",
        builtIn: true,
      },
    ];
  }
  let selectedId =
    templates.find((t) => t.id === options.selectedTemplateId)?.id ||
    templates[0].id;
  const deletedIds = new Set<string>();
  let resetBuiltin = false;
  const seedBody = options.seedBody || "";
  const seedBootstrapBody = options.seedBootstrapBody || "";

  const chipHtml = () =>
    templates
      .map(
        (t) =>
          `<button type="button" class="year-chip${t.id === selectedId ? " active" : ""}" data-sfe-template-select="${esc(t.id)}" aria-pressed="${t.id === selectedId ? "true" : "false"}">${esc(t.name)}</button>`,
      )
      .join("");

  const selected = () =>
    templates.find((t) => t.id === selectedId) || templates[0];

  return new Promise((resolve) => {
    const overlay = mountOverlay(`
      <div class="sfe-modal sfe-confirm-modal sfe-instructions-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-instructions-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-instructions-title">${esc(title)}</h3>
          <button type="button" class="sfe-modal-close" data-sfe-confirm="cancel" aria-label="Close">×</button>
        </div>
        ${
          options.message
            ? `<p class="sfe-confirm-message">${esc(options.message)}</p>`
            : ""
        }
        <div class="sfe-instructions-dialog-body">
          <div class="sfe-instructions-template-block">
            <h4 class="sfe-instructions-template-title">${esc(templateLabel)}</h4>
            <p class="muted small">${esc(templateHint)}</p>
            <div class="sfe-instructions-template-select" data-sfe-template-chips>
              ${chipHtml()}
            </div>
            <div class="sfe-instructions-template-toolbar">
              <label class="sfe-modal-field sfe-instructions-template-name-field">
                <span class="sfe-confirm-field-label">Template name</span>
                <input
                  type="text"
                  class="sfe-api-key-input sfe-confirm-input"
                  data-sfe-template-name
                  maxlength="80"
                  value="${esc(selected().name)}"
                />
              </label>
              <div class="sfe-instructions-template-actions">
                <button type="button" class="sfe-btn small" data-sfe-template-action="new" title="Duplicate current template">New template</button>
                <button type="button" class="sfe-btn small" data-sfe-template-action="reset" title="Reset built-in Wheel template" ${selected().builtIn ? "" : "hidden"}>Reset</button>
                <button type="button" class="sfe-btn small" data-sfe-template-action="delete" title="Delete this template" ${selected().builtIn || templates.length <= 1 ? "hidden" : ""}>Delete</button>
              </div>
            </div>
            <label class="sfe-modal-field sfe-textarea-field">
              <span class="sfe-confirm-field-label">${esc(bootstrapLabel)}</span>
              <p class="muted small sfe-instructions-field-hint">${esc(bootstrapHint)}</p>
              <textarea
                class="sfe-api-key-input sfe-confirm-input sfe-confirm-textarea sfe-instructions-template-textarea sfe-instructions-bootstrap-textarea"
                data-sfe-template-bootstrap
                rows="10"
                spellcheck="false"
              >${esc(selected().bootstrapBody || "")}</textarea>
            </label>
            <label class="sfe-modal-field sfe-textarea-field">
              <span class="sfe-confirm-field-label">${esc(slotLabel)}</span>
              <p class="muted small sfe-instructions-field-hint">${esc(slotHint)}</p>
              <textarea
                class="sfe-api-key-input sfe-confirm-input sfe-confirm-textarea sfe-instructions-template-textarea"
                data-sfe-template-body
                rows="12"
                spellcheck="false"
              >${esc(selected().body)}</textarea>
            </label>
            ${
              legend
                ? `<details class="sfe-instructions-placeholder-legend">
              <summary class="muted small">Placeholder key</summary>
              <pre class="sfe-advise-log-pre sfe-instructions-template-pre" tabindex="0">${esc(legend)}</pre>
            </details>`
                : ""
            }
          </div>
          <label class="sfe-modal-field sfe-textarea-field">
            <span class="sfe-confirm-field-label">${esc(fieldLabel)}</span>
            <p class="muted small sfe-instructions-field-hint">${esc(instructionsHint)}</p>
            <textarea
              class="sfe-api-key-input sfe-confirm-input sfe-confirm-textarea"
              data-sfe-instructions-input
              maxlength="${maxLength}"
              rows="${rows}"
              placeholder="${esc(options.placeholder || "")}"
              spellcheck="true"
            >${esc(defaultValue)}</textarea>
          </label>
        </div>
        <p class="muted small sfe-textarea-hint">Save writes bootstrap + slot template + instructions · Ctrl/⌘+Enter · Esc to cancel</p>
        <div class="sfe-modal-actions">
          <button type="button" class="sfe-btn" data-sfe-confirm="cancel">${esc(cancelLabel)}</button>
          <button type="button" class="sfe-btn sfe-btn-primary" data-sfe-confirm="ok">${esc(confirmLabel)}</button>
        </div>
      </div>
    `);

    const input = overlay.querySelector<HTMLTextAreaElement>("[data-sfe-instructions-input]");
    const nameInput = overlay.querySelector<HTMLInputElement>("[data-sfe-template-name]");
    const bootstrapInput = overlay.querySelector<HTMLTextAreaElement>(
      "[data-sfe-template-bootstrap]",
    );
    const bodyInput = overlay.querySelector<HTMLTextAreaElement>("[data-sfe-template-body]");
    const chipsEl = overlay.querySelector<HTMLElement>("[data-sfe-template-chips]");
    const resetBtn = overlay.querySelector<HTMLElement>('[data-sfe-template-action="reset"]');
    const deleteBtn = overlay.querySelector<HTMLElement>('[data-sfe-template-action="delete"]');

    const flushEditorToSelected = () => {
      const cur = selected();
      if (!cur) return;
      if (nameInput) cur.name = nameInput.value.trim() || cur.name;
      if (bootstrapInput) {
        cur.bootstrapBody = bootstrapInput.value.replace(/\r\n/g, "\n");
      }
      if (bodyInput) cur.body = bodyInput.value.replace(/\r\n/g, "\n");
    };

    const refreshChrome = () => {
      if (chipsEl) chipsEl.innerHTML = chipHtml();
      const cur = selected();
      if (nameInput) nameInput.value = cur.name;
      if (bootstrapInput) bootstrapInput.value = cur.bootstrapBody || "";
      if (bodyInput) bodyInput.value = cur.body;
      if (resetBtn) resetBtn.hidden = !cur.builtIn;
      if (deleteBtn) deleteBtn.hidden = Boolean(cur.builtIn) || templates.length <= 1;
    };

    let settled = false;
    const finish = (value: InstructionsDialogResult | null) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey, true);
      removeOverlay();
      resolve(value);
    };

    const submit = () => {
      flushEditorToSelected();
      const cur = selected();
      const raw = (input?.value ?? "")
        .replace(/\r\n/g, "\n")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
      if (!String(cur.bootstrapBody || "").trim()) {
        bootstrapInput?.focus();
        return;
      }
      if (!cur.body.trim()) {
        bodyInput?.focus();
        return;
      }
      finish({
        instructions: raw.slice(0, maxLength),
        promptTemplateId: cur.id,
        templates: templates.map((t) => ({ ...t })),
        deletedIds: [...deletedIds],
        resetBuiltin,
      });
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        finish(null);
      } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        submit();
      }
    };

    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        finish(null);
        return;
      }
      const selectId = target.closest<HTMLElement>("[data-sfe-template-select]")?.dataset
        .sfeTemplateSelect;
      if (selectId) {
        flushEditorToSelected();
        selectedId = selectId;
        refreshChrome();
        return;
      }
      const tmplAction = target.closest<HTMLElement>("[data-sfe-template-action]")?.dataset
        .sfeTemplateAction;
      if (tmplAction === "new") {
        flushEditorToSelected();
        const src = selected();
        const id = `local-${Date.now().toString(36)}`;
        templates.push({
          id,
          name: `${src.name} copy`,
          body: src.body,
          bootstrapBody: src.bootstrapBody || seedBootstrapBody,
          builtIn: false,
        });
        selectedId = id;
        refreshChrome();
        nameInput?.focus();
        return;
      }
      if (tmplAction === "reset") {
        const cur = selected();
        if (!cur.builtIn || !seedBody) return;
        cur.name = "Wheel (default)";
        cur.body = seedBody;
        cur.bootstrapBody = seedBootstrapBody || cur.bootstrapBody;
        resetBuiltin = true;
        refreshChrome();
        return;
      }
      if (tmplAction === "delete") {
        flushEditorToSelected();
        const cur = selected();
        if (cur.builtIn || templates.length <= 1) return;
        if (!cur.id.startsWith("local-")) deletedIds.add(cur.id);
        templates = templates.filter((t) => t.id !== cur.id);
        selectedId = templates[0].id;
        refreshChrome();
        return;
      }
      const action = target.closest<HTMLElement>("[data-sfe-confirm]")?.dataset.sfeConfirm;
      if (action === "ok") submit();
      if (action === "cancel") finish(null);
    });

    document.addEventListener("keydown", onKey, true);
    requestAnimationFrame(() => {
      bootstrapInput?.focus();
    });
  });
}

/**
 * Read-only content dialog (scrollable HTML body). Resolves when closed.
 */
export function contentDialog(options: ContentDialogOptions): Promise<void> {
  const title = options.title?.trim() || "Details";
  const closeLabel = options.closeLabel?.trim() || "Close";
  const modalClass = ["sfe-modal", "sfe-confirm-modal", "sfe-content-modal", options.modalClass]
    .filter(Boolean)
    .join(" ");

  return new Promise((resolve) => {
    const overlay = mountOverlay(`
      <div class="${modalClass}" role="dialog" aria-modal="true" aria-labelledby="sfe-content-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-content-title">${esc(title)}</h3>
          <button type="button" class="sfe-modal-close" data-sfe-confirm="cancel" aria-label="Close">×</button>
        </div>
        ${
          options.message
            ? `<p class="sfe-confirm-message" id="sfe-content-message">${esc(options.message)}</p>`
            : ""
        }
        <div class="sfe-content-dialog-body">${options.bodyHtml || ""}</div>
        <div class="sfe-modal-actions">
          ${options.footerHtml || ""}
          <button type="button" class="sfe-btn sfe-btn-primary" data-sfe-confirm="ok" autofocus>${esc(closeLabel)}</button>
        </div>
      </div>
    `);

    const runAction = async (
      runner: ContentDialogOptions["onBodyAction"] | ContentDialogOptions["onFooterAction"],
      ...args: [string, string, string, HTMLElement] | [string, HTMLElement]
    ) => {
      if (!runner) return;
      const result = await (args.length === 4
        ? (runner as NonNullable<ContentDialogOptions["onBodyAction"]>)(
            args[0],
            args[1],
            args[2],
            args[3],
          )
        : (runner as NonNullable<ContentDialogOptions["onFooterAction"]>)(args[0], args[1]));
      if (result === true) finish();
    };

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey, true);
      removeOverlay();
      resolve();
    };

    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      // Enter must stay available for typing when the body holds a field.
      const typing =
        target instanceof HTMLElement &&
        (target.tagName === "TEXTAREA" || target.tagName === "INPUT" || target.isContentEditable);
      if (event.key === "Escape" || (event.key === "Enter" && !typing)) {
        event.preventDefault();
        finish();
      }
    };

    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        finish();
        return;
      }
      const adviceBtn = target.closest<HTMLElement>("[data-sfe-advice-action]");
      if (adviceBtn) {
        event.preventDefault();
        event.stopPropagation();
        const action = adviceBtn.dataset.sfeAdviceAction || "";
        const recId = adviceBtn.dataset.recId || "";
        const agentId = adviceBtn.dataset.agentId || "";
        if (recId && options.onBodyAction) {
          void runAction(options.onBodyAction, action, recId, agentId, adviceBtn);
          return;
        }
        if (options.onFooterAction) {
          void runAction(options.onFooterAction, action, adviceBtn);
        }
        return;
      }
      const action = target.closest<HTMLElement>("[data-sfe-confirm]")?.dataset.sfeConfirm;
      if (action === "ok" || action === "cancel") finish();
    });

    document.addEventListener("keydown", onKey, true);
    overlay.querySelector<HTMLButtonElement>('[data-sfe-confirm="ok"]')?.focus();
  });
}
